import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import DuplaConfirmacao from "src/v2/components/DuplaConfirmacao/DuplaConfirmacao"
import ButtonWithIcon from "src/v2/components/Button/Button"
import theme from "src/v2/theme/theme"
import { COMPANY_REGISTRATION_CONFIG } from "./company-registration-config"
import CompanyRegistrationForm from "./company-registration-form"
import CompanyRegistrationTable from "./company-registration-table"
import useCompanyRegistrationTable from "./use-company-registration-table"

const CompanyRegistrationModal = ({ open, registrationType, companyIdentifier, onClose, onContraparteSaved }) => {
    const definition = COMPANY_REGISTRATION_CONFIG[registrationType]
    const isDirectCounterpart = registrationType === "CONTRAPARTE"
    const [editor, setEditor] = useState(null)
    const [directEditor, setDirectEditor] = useState(null)
    const [viewer, setViewer] = useState(null)
    const [rowToDelete, setRowToDelete] = useState(null)
    const table = useCompanyRegistrationTable({
        definition,
        companyIdentifier,
        onSaved: isDirectCounterpart ? onContraparteSaved : undefined,
    })

    useEffect(() => {
        setEditor(null)
        setDirectEditor(null)
        setViewer(null)
        setRowToDelete(null)
    }, [companyIdentifier, open, registrationType])

    useEffect(() => {
        if (!open || !isDirectCounterpart || table.loading || !table.hasLoaded) return

        const row = table.rows[0]
        setDirectEditor(row ? { companyIdentifier, row } : null)
    }, [companyIdentifier, isDirectCounterpart, open, table.hasLoaded, table.loading, table.rows])

    if (!definition) return null

    if (isDirectCounterpart) {
        const directRow = directEditor?.companyIdentifier === companyIdentifier ? directEditor.row : null

        if (directRow) {
            return (
                <CompanyRegistrationForm
                    open={open}
                    definition={definition}
                    initialRow={directRow}
                    isNew={false}
                    saving={table.isMutating}
                    onClose={onClose}
                    onSave={(payload) => table.save({ row: { ID: directRow.ID, ...payload }, isNew: false })}
                />
            )
        }

        return (
            <Dialog
                open={open}
                onClose={table.isMutating ? undefined : onClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, color: theme.palette.common.white } }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                    <Typography component="div" variant="h3" color="common.white">Contraparte</Typography>
                    <IconButton
                        aria-label="Fechar Contraparte"
                        onClick={onClose}
                        disabled={table.isMutating}
                        sx={{
                            color: theme.palette.icon.main,
                            "&:focus-visible": {
                                outline: `2px solid ${theme.palette.common.white}`,
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography
                        role={table.error ? "alert" : "status"}
                        color="common.white"
                    >
                        {table.error
                            ? table.error
                            : table.loading || !table.hasLoaded
                                ? "Carregando Contraparte..."
                                : "Nenhuma Contraparte cadastrada para esta empresa."}
                    </Typography>
                    {table.error && (
                        <ButtonWithIcon
                            size="md"
                            onClick={() => table.loadPage({ nextPage: 1, nextPageSize: table.pageSize })}
                            sx={{ mt: 2 }}
                        >
                            TENTAR NOVAMENTE
                        </ButtonWithIcon>
                    )}
                </DialogContent>
            </Dialog>
        )
    }

    const handleAdd = () => {
        if (!definition.allowCreate) return

        const createPayload = definition.createPayload?.() ?? {}

        setEditor({ isNew: true, row: { ...createPayload, CPFCNPJ: companyIdentifier } })
    }
    const handleEdit = (row) => setEditor({ isNew: false, row })
    const handleConfirmDelete = async () => {
        const deleted = await table.remove(rowToDelete)
        if (deleted) setRowToDelete(null)
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={table.isMutating ? undefined : onClose}
                fullWidth
                maxWidth={false}
                PaperProps={{
                    sx: {
                        m: { xxxs: 0, sm: 2, md: 4 },
                        width: { xxxs: "100vw", sm: "calc(100vw - 32px)", md: "min(1480px, calc(100vw - 64px))" },
                        maxWidth: "none",
                        maxHeight: { xxxs: "100dvh", sm: "calc(100dvh - 32px)" },
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.common.white,
                    },
                }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: { xxxs: 2, sm: 3 }, pt: { xxxs: 2, sm: 3 } }}>
                    <Typography component="div" variant="h3" color="common.white">{definition.title}</Typography>
                    <IconButton aria-label={`Fechar ${definition.title}`} onClick={onClose} disabled={table.isMutating} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: { xxxs: 2, sm: 3 }, pb: { xxxs: 2, sm: 3 }, overflow: "hidden" }}>
                    <CompanyRegistrationTable
                        definition={definition}
                        table={table}
                        onAdd={handleAdd}
                        onView={setViewer}
                        onEdit={handleEdit}
                        onDelete={setRowToDelete}
                    />
                </DialogContent>
            </Dialog>

            {editor && (
                <CompanyRegistrationForm
                    open
                    definition={definition}
                    initialRow={editor.row}
                    isNew={editor.isNew}
                    saving={table.isMutating}
                    onClose={() => setEditor(null)}
                    onSave={(payload) => table.save({
                        row: editor.isNew ? payload : { ID: editor.row.ID, ...payload },
                        isNew: editor.isNew,
                    })}
                />
            )}
            {viewer && (
                <CompanyRegistrationForm
                    open
                    definition={definition}
                    initialRow={viewer}
                    isNew={false}
                    mode="readOnly"
                    saving={false}
                    onClose={() => setViewer(null)}
                    onSave={() => false}
                />
            )}
            <DuplaConfirmacao
                open={Boolean(rowToDelete)}
                title="Excluir registro"
                description="Esta ação não pode ser desfeita. Deseja excluir este registro?"
                cancel={() => setRowToDelete(null)}
                confirm={handleConfirmDelete}
                confirmTextButton={table.isMutating ? "EXCLUINDO..." : "EXCLUIR"}
            />
        </>
    )
}

CompanyRegistrationModal.propTypes = {
    open: PropTypes.bool.isRequired,
    registrationType: PropTypes.oneOf(Object.keys(COMPANY_REGISTRATION_CONFIG)).isRequired,
    companyIdentifier: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onContraparteSaved: PropTypes.func,
}

CompanyRegistrationModal.defaultProps = {
    companyIdentifier: "",
    onContraparteSaved: undefined,
}

export default CompanyRegistrationModal
